import { type TypedDocumentNode, gql } from '@apollo/client';
import { differenceInDays } from 'date-fns';
import { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  HeadlineL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import Dialog from '@sorare/core/src/components/dialog';
import { FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LobbyFixturePickerItem } from '@sorare/gameplay/src/components/fixture/LobbyFixturePickerItem';

import type {
  PlayNowOrLater_teamInterface,
  PlayNowOrLater_userSportProfileInterface,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
  background: var(--c-nd-100-opaque);

  @media ${laptopAndAbove} {
    padding: var(--quadruple-unit) var(--double-unit);
  }
`;
const ChooseFixtureBlock = styled(Vertical).attrs({ center: true, gap: 1.5 })`
  position: relative;
  height: 132px;
  flex: 1;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-50);
  overflow: hidden;
  isolation: isolate;
  justify-content: flex-end;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.2;
    z-index: -1;
    background:
      linear-gradient(var(--c-nd-50), var(--c-nd-50)),
      center / 100% no-repeat var(--background) lightgray;
  }

  &.grey::before {
    filter: grayscale(1);
  }
`;
const StyledLink = styled(Link)`
  flex: 1;
  display: flex;
  justify-content: stretch;
  max-width: 50%;
`;
const CTA = styled(Horizontal).attrs({ center: true })`
  width: 100%;
  height: calc(5 * var(--unit));
  display: flex;
`;
const PrimaryCTA = styled(CTA)`
  background-color: var(--c-brand-600);
`;
const SecondaryCTA = styled(CTA)`
  background-color: var(--c-white);
  color: var(--c-black);
`;

type Props = {
  team: PlayNowOrLater_teamInterface;
  userSportProfile: PlayNowOrLater_userSportProfileInterface;
};
const PlayNowOrLaterContent = ({ team, userSportProfile }: Props) => {
  const nextGameDate = team.upcomingAnyGames.at(0)?.date;
  const numberOfDaysUntilNextGame =
    nextGameDate && differenceInDays(nextGameDate, new Date());
  const { myFavoriteTeamOnboardingSo5Leaderboard, onboardingSo5Leaderboard } =
    userSportProfile;

  const teamHasNextFixture =
    myFavoriteTeamOnboardingSo5Leaderboard && !!numberOfDaysUntilNextGame;
  const { shortName, pictureUrl } = team;

  return (
    <Root className="text-center">
      <Vertical gap={0.5}>
        <HeadlineL>
          {teamHasNextFixture ? (
            <FormattedMessage
              id="PlayNowOrLater.title"
              defaultMessage="Play now or later?"
            />
          ) : (
            <FormattedMessage
              id="PlayNowOrLater.alternativeTitle"
              defaultMessage="{clubName} are not playing soon"
              values={{ clubName: shortName }}
            />
          )}
        </HeadlineL>
        <BodyM color="var(--c-nd-600)">
          {teamHasNextFixture ? (
            <FormattedMessage
              id="PlayNowOrLater.subtitle"
              defaultMessage="{clubName} play in {nbDays} days. Do you want to wait until then, or play <bold>now</bold> with different players?"
              values={{
                clubName: shortName,
                nbDays: numberOfDaysUntilNextGame,
                bold: Bold,
              }}
            />
          ) : (
            <FormattedMessage
              id="PlayNowOrLater.alternativeSubtitle"
              defaultMessage="Play now with players from other teams instead."
            />
          )}
        </BodyM>
      </Vertical>
      <Horizontal gap={1.5} center>
        {onboardingSo5Leaderboard && (
          <StyledLink
            to={generatePath(FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE_SLUG, {
              slug: onboardingSo5Leaderboard.slug,
            })}
            keepSearchParams
          >
            <ChooseFixtureBlock
              style={
                {
                  '--background': `url(${pictureUrl})`,
                } as CSSProperties
              }
              className="grey"
            >
              <LobbyFixturePickerItem
                so5Fixture={onboardingSo5Leaderboard.so5Fixture}
                selected
              />
              <PrimaryCTA>
                <LabelM bold>
                  <FormattedMessage {...glossary.playNow} />
                </LabelM>
              </PrimaryCTA>
            </ChooseFixtureBlock>
          </StyledLink>
        )}
        {teamHasNextFixture && (
          <StyledLink
            to={generatePath(FOOTBALL_PLAY_PRO_ONBOARDING_COMPOSE_SLUG, {
              slug: myFavoriteTeamOnboardingSo5Leaderboard.slug,
            })}
            keepSearchParams
          >
            <ChooseFixtureBlock
              style={
                {
                  '--background': `url(${team.pictureUrl})`,
                } as CSSProperties
              }
            >
              <LobbyFixturePickerItem
                so5Fixture={myFavoriteTeamOnboardingSo5Leaderboard.so5Fixture}
                opaque
              />
              <SecondaryCTA>
                <LabelM bold>
                  <FormattedMessage {...glossary.play} />
                </LabelM>
              </SecondaryCTA>
            </ChooseFixtureBlock>
          </StyledLink>
        )}
      </Horizontal>
      <BodyS color="var(--c-nd-600)">
        <FormattedMessage
          id="PlayNowOrLater.extraInfo"
          defaultMessage="You can still play with {clubName} later if you play now."
          values={{ clubName: team.shortName }}
        />
      </BodyS>
    </Root>
  );
};

export const PlayNowOrLater = (props: Props) => {
  const isLaptopAndAbove = useIsLaptop();

  if (isLaptopAndAbove) {
    return (
      <Dialog open maxWidth="xs" fullWidth>
        <PlayNowOrLaterContent {...props} />
      </Dialog>
    );
  }

  return (
    <Dropdown open label={() => null}>
      <PlayNowOrLaterContent {...props} />
    </Dropdown>
  );
};

PlayNowOrLater.fragments = {
  teamInterface: gql`
    fragment PlayNowOrLater_teamInterface on TeamInterface {
      slug
      pictureUrl
      shortName
      upcomingAnyGames(first: 1) {
        id
        date
      }
    }
  ` as TypedDocumentNode<PlayNowOrLater_teamInterface>,
  userSportProfileInterface: gql`
    fragment PlayNowOrLater_userSportProfileInterface on UserSportProfileInterface {
      id
      onboardingSo5Leaderboard {
        slug
        so5Fixture {
          slug
          ...LobbyFixturePickerItem_so5Fixture
        }
      }
      myFavoriteTeamOnboardingSo5Leaderboard {
        slug
        so5Fixture {
          slug
          ...LobbyFixturePickerItem_so5Fixture
        }
      }
    }
    ${LobbyFixturePickerItem.fragments.so5Fixture}
  ` as TypedDocumentNode<PlayNowOrLater_userSportProfileInterface>,
};
